import { utils as glUtils } from "@hscmap/gl-wrapper";
import { hips, Layer, normalizeZenith, View } from "@hscmap/stellar-globe";
import { Component, Prop, Watch } from "vue-property-decorator";
import { LayerComponentBase } from "./layer_component_base";

class HipsLayer extends Layer {
  hr?: hips.Renderer

  integerOrder = false
  lodBias = 0
  animationLod = 1
  fade = true

  constructor(
    readonly baseUrl: string,
    public keepCenter: boolean,
    readonly R: typeof hips.Renderer,
  ) {
    super()
  }

  async onAdd() {
    const tp = await hips.SimpleImageTextureProvider.fromBaseUrl(this.globe, this.baseUrl)
    if (this.globe) {
      this.hr = new this.R(this.globe, tp)
      const coord = this.globe.viewFactory.center()
      const keepFovy = true
      if ('hsc/zenith' in tp.properties) {
        const [a, d, p] = JSON.parse(tp.properties['hsc/zenith']) as number[]
        const [za, zd, zp] = normalizeZenith(a, d, p)
        this.globe.viewFactory.jumpTo({ za, zd, zp }, { keepFovy, ...(this.keepCenter ? { coord } : {}) })
      } else {
        this.globe.viewFactory.jumpTo({ za: 0, zd: Math.PI / 2, zp: 0 }, { coord, keepFovy })
      }
    } else {
      tp.release()
    }
  }

  onRemove() {
    if (this.hr) {
      this.hr.textureProvider.release()
      this.hr.release()
      this.hr = undefined
    }
  }

  protected alpha(view: View) {
    return this.fade ? Layer.overlayAlpha(view) : 1
  }

  render(view: View) {
    const { hr } = this
    if (hr) {
      hr.integerOrder = this.integerOrder
      hr.lodBias = this.lodBias
      hr.animationLod = this.animationLod
      hr.alpha = this.alpha(view)
      hr.render(view)
    }
  }
}

abstract class HipsLayerComponentBase extends LayerComponentBase<HipsLayer> {
  @Prop({ required: true })
  baseUrl!: string
  @Watch('baseUrl')
  onBaseUrlChange() {
    this.refreshLayer()
  }

  @Prop({ default: false })
  keepCenter!: boolean
  @Watch('keepCenter')
  onKeepCenterChange() {
    this.layer.keepCenter = this.keepCenter
  }

  @Prop({ default: 0 })
  lodBias!: number
  @Watch('lodBias')
  onLodBiasChange() {
    this.layer.lodBias = this.lodBias
    this.globe.requestRefresh()
  }

  @Prop({ default: 1 })
  animationLod!: number
  @Watch('animationLod')
  onanimationLodChange() {
    this.layer.animationLod = this.animationLod
    this.globe.requestRefresh()
  }

  @Prop({ default: false })
  integerOrder!: boolean
  @Watch('integerOrder')
  onintegerOrderChange() {
    this.layer.integerOrder = this.integerOrder
    this.globe.requestRefresh()
  }

  @Prop({ default: true })
  fade!: boolean
  @Watch('fade')
  onFadeChange() {
    this.layer.fade = this.fade
    this.globe.requestRefresh()
  }

  newLayer() {
    const l = new HipsLayer(this.baseUrl, this.keepCenter, this.Renderer())
    l.lodBias = this.lodBias
    l.animationLod = this.animationLod
    l.integerOrder = this.integerOrder
    l.fade = this.fade
    return l
  }

  abstract Renderer(): typeof hips.Renderer
}

@Component
export class HipsPngLayerComponent extends HipsLayerComponentBase {
  Renderer() {
    return hips.Renderer
  }
}

@Component
export class HipsMaskLayerComponent extends HipsLayerComponentBase {
  Renderer() {
    return MultiplyRenderer
  }
}

class MultiplyRenderer extends hips.Renderer {
  protected activateProgram(cb: () => void) {
    const gl = this.gl
    this.program.use()
    this.attribList.enable(this.program, () => {
      glUtils.enable(gl, [gl.BLEND], () => {
        gl.blendFunc(gl.ZERO, gl.SRC_COLOR)
        cb()
      })
    })
  }
}
